import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Sparkles,
  LayoutTemplate,
  Zap,
  SlidersHorizontal,
  Type,
  Palette,
  Smartphone,
  UserRound,
  Users,
  PersonStanding,
  Camera,
  Layers,
  Boxes,
  type LucideIcon,
} from "lucide-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { botonDeLaPantalla, type BotonDeNovedad } from "../lib/novedades";
import { heicTo } from "heic-to";

interface PuntoClave {
  /** Clave del ícono lucide (ver PUNTO_ICONS). */
  icono: string;
  texto: string;
}

interface MessageItem {
  titulo: string;
  descripcion?: string;
  url_mobile?: string;
  url_desktop?: string;
  /** Puntos clave (ícono + texto corto) que se listan debajo de la descripción. */
  puntos?: PuntoClave[];
  /** Botón opcional a una ruta de Atlas (ADR-2026-11-02-boton-en-el-modal-de-novedades). */
  boton?: BotonDeNovedad;
}

interface UpdateInfoProps {
  isOpen: boolean;
  onClose: () => void;
  message: {
    title?: string;
    title_description?: string;
    items?: MessageItem[];
    [key: string]: any;
  };
  userId: string;
}

/** True when viewport width < 640px (matches Tailwind sm breakpoint) */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 640,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
};

/** True if URL points to a video file */
const isVideoUrl = (url: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url);

/** True if URL points to a HEIC/HEIF file */
const isHeicUrl = (url: string) => /\.(heic|heif)(\?|$)/i.test(url);

/** Fetches a remote HEIC image, converts to JPEG blob URL */
const HeicImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ src, alt, className, style }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoke = "";
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        const jpegBlob = await heicTo({
          blob,
          type: "image/jpeg",
          quality: 0.85,
        });
        if (cancelled) return;
        const url = URL.createObjectURL(jpegBlob);
        revoke = url;
        setBlobUrl(url);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [src]);

  if (error)
    return (
      <div className="w-full h-40 bg-neutral-100 flex items-center justify-center">
        <span className="text-[13px] text-brand-black/40">
          No se pudo cargar la imagen
        </span>
      </div>
    );
  if (!blobUrl)
    return (
      <div className="w-full h-40 bg-neutral-100 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
      </div>
    );
  return <img src={blobUrl} alt={alt} className={className} style={style} />;
};

/** Renders the media element (video / heic image / regular image) */
const MediaElement: React.FC<{ url: string; alt: string }> = ({ url, alt }) => {
  // Anchor to the right only for videos where the right side has the important content
  const anchorRight = /masiva/i.test(url);
  const positionClass = anchorRight ? "object-right" : "object-center";
  const baseClass = `absolute inset-0 w-full h-full object-cover ${positionClass}`;
  if (isVideoUrl(url)) {
    return (
      <video
        key={url}
        src={url}
        autoPlay
        muted
        loop
        playsInline
        className={baseClass}
      />
    );
  }
  if (isHeicUrl(url)) {
    return <HeicImage key={url} src={url} alt={alt} className={baseClass} />;
  }
  return <img src={url} alt={alt} className={baseClass} />;
};

/** Mapa nombre→ícono lucide para los puntos clave. Si la clave no existe,
 *  cae a Sparkles (así un typo en el JSON no rompe el render). */
const PUNTO_ICONS: Record<string, LucideIcon> = {
  "layout-template": LayoutTemplate,
  zap: Zap,
  sliders: SlidersHorizontal,
  type: Type,
  palette: Palette,
  smartphone: Smartphone,
  user: UserRound,
  users: Users,
  pose: PersonStanding,
  camera: Camera,
  layers: Layers,
  boxes: Boxes,
};

/** Lista de puntos clave (ícono lucide en chip + texto corto), estilo ATLAS.
 *  Compacta a propósito para que entre bien en mobile. */
const PuntosList: React.FC<{
  puntos?: PuntoClave[];
  className?: string;
}> = ({ puntos, className }) => {
  if (!puntos || puntos.length === 0) return null;
  return (
    <ul className={`flex flex-col gap-2.5 text-left ${className ?? ""}`}>
      {puntos.map((p, i) => {
        const Icon = PUNTO_ICONS[p.icono] ?? Sparkles;
        return (
          <li key={i} className="flex items-center gap-2.5">
            <span className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-lg bg-brand-primary/[0.08] text-brand-primary">
              <Icon className="w-4 h-4" strokeWidth={2} />
            </span>
            <span className="text-[13.5px] sm:text-[14px] text-brand-black/75 leading-snug">
              {p.texto}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export const UpdateInfoModal: React.FC<UpdateInfoProps> = ({
  isOpen,
  onClose,
  message,
  userId,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const items = useMemo(() => message.items ?? [], [message.items]);

  // 'welcome' = initial title screen, 0..N-1 = item index
  const [step, setStep] = useState<"welcome" | number>("welcome");
  // Animation direction: 1 = forward, -1 = backward
  const [direction, setDirection] = useState(1);
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) setStep("welcome");
  }, [isOpen]);

  // Cleanup timeout
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const goTo = useCallback(
    (next: "welcome" | number, dir: number) => {
      if (animating) return;
      setDirection(dir);
      setAnimating(true);
      timeoutRef.current = setTimeout(() => {
        setStep(next);
        setAnimating(false);
      }, 250);
    },
    [animating],
  );

  const handleClose = async () => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ home_message_seen: true })
        .eq("id", userId);
      if (error) console.error("Error updating message status:", error);
      onClose();
    } catch (err) {
      console.error("Error in closing update info modal:", err);
      onClose();
    }
  };

  // El botón de la pantalla: marca el mensaje como visto (igual que cerrar) y lleva a su ruta.
  const irAlBoton = async (b: BotonDeNovedad) => {
    await handleClose();
    navigate(b.ruta);
  };

  if (!isOpen) return null;

  const currentItem = typeof step === "number" ? items[step] : null;
  const boton = currentItem ? botonDeLaPantalla(currentItem.boton) : null;
  // Con botón, "Siguiente"/"Entendido" pasa a secundario: el mismo borde que el de volver.
  const claseNavegar = boton
    ? "border border-neutral-200 bg-white hover:bg-neutral-50 text-brand-black/75"
    : "bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm";
  const isLastItem = typeof step === "number" && step === items.length - 1;

  /** Pick the best URL for the current viewport */
  const getMediaUrl = (item: MessageItem): string | null => {
    const primary = isMobile ? item.url_mobile : item.url_desktop;
    const fallback = isMobile ? item.url_desktop : item.url_mobile;
    const url =
      (primary && primary.trim()) || (fallback && fallback.trim()) || null;
    return url || null;
  };

  // Transition classes
  const contentClass = animating
    ? `transition-all duration-250 ease-out ${direction === 1 ? "opacity-0 translate-x-4" : "opacity-0 -translate-x-4"}`
    : "transition-all duration-250 ease-out opacity-100 translate-x-0";

  // Navigation bar (mobile item screens only — dots are shown above the title)
  const navigationBar = typeof step === "number" && (
    <div className="shrink-0 px-5 pb-4 pt-3">
      {boton && (
        <button
          type="button"
          onClick={() => irAlBoton(boton)}
          className="mb-3 w-full inline-flex items-center justify-center h-[44px] rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors"
        >
          {boton.texto}
        </button>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => goTo(step === 0 ? "welcome" : step - 1, -1)}
          className="inline-flex items-center justify-center w-[44px] h-[44px] rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-brand-black/60 transition-colors shrink-0"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        {isLastItem ? (
          <button
            type="button"
            onClick={handleClose}
            className={`flex-1 inline-flex items-center justify-center h-[44px] rounded-md text-[15px] font-medium transition-colors ${claseNavegar}`}
          >
            Entendido
          </button>
        ) : (
          <button
            type="button"
            onClick={() => goTo(step + 1, 1)}
            className={`flex-1 inline-flex items-center justify-center gap-2 h-[44px] rounded-md text-[15px] font-medium transition-colors ${claseNavegar}`}
          >
            Siguiente
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      {/* ══ Modal container — fixed size per breakpoint ══ */}
      <div
        className="bg-white rounded-lg shadow-[0_8px_40px_rgba(0,0,0,0.12)] relative flex flex-col overflow-hidden
          w-[min(360px,calc(100vw-32px))] sm:w-[1050px] sm:max-w-[90vw]"
        style={{ height: isMobile ? "min(700px, 92vh)" : "min(580px, 85vh)" }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* ═══════ MOBILE LAYOUT ═══════ */}
        {isMobile && step === "welcome" && (
          <>
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              {message.title && (
                <h2 className="text-[22px] font-semibold text-brand-black/90 leading-snug">
                  {message.title}
                </h2>
              )}
              {message.title_description && (
                <p className="text-[15px] text-brand-black/50 mt-2 max-w-xs">
                  {message.title_description}
                </p>
              )}
            </div>
            <div className="shrink-0 flex flex-col items-center px-6 pb-6">
              <div className="w-56 h-56">
                <DotLottieReact
                  src="/images/lottie_animations/Rocket Launch.lottie"
                  autoplay
                  loop
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
              {items.length > 0 ? (
                <button
                  type="button"
                  onClick={() => goTo(0, 1)}
                  className="w-full inline-flex items-center justify-center gap-2 h-[44px] rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors"
                >
                  Ver novedades
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full inline-flex items-center justify-center h-[44px] rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors"
                >
                  Entendido
                </button>
              )}
            </div>
          </>
        )}
        {isMobile &&
          typeof step === "number" &&
          currentItem &&
          (() => {
            const mediaUrl = getMediaUrl(currentItem);
            const hasPuntos = !!(
              currentItem.puntos && currentItem.puntos.length
            );
            return (
              <>
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                  <div className={contentClass}>
                    <div className="flex flex-col">
                      {mediaUrl && (
                        <div
                          className="overflow-hidden bg-neutral-100 relative w-full"
                          style={
                            hasPuntos
                              ? { height: "38vh" }
                              : { aspectRatio: "3/4" }
                          }
                        >
                          <MediaElement
                            url={mediaUrl}
                            alt={currentItem.titulo}
                          />
                        </div>
                      )}
                      <div className="px-5 pt-4 pb-2 text-center">
                        {items.length > 1 && (
                          <div className="flex items-center justify-center gap-1.5 mb-3">
                            {items.map((_, i) => (
                              <div
                                key={i}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                  i === step
                                    ? "w-5 bg-brand-primary"
                                    : "w-1.5 bg-neutral-300"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                        <h3 className="text-[17px] font-semibold text-brand-black/90 leading-snug">
                          {currentItem.titulo}
                        </h3>
                        {currentItem.descripcion && (
                          <p className="text-[15px] text-brand-black/50 mt-1">
                            {currentItem.descripcion}
                          </p>
                        )}
                        <PuntosList
                          puntos={currentItem.puntos}
                          className="mt-4 mx-auto max-w-[300px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {navigationBar}
              </>
            );
          })()}

        {/* ═══════ DESKTOP LAYOUT ═══════ */}
        {!isMobile && (
          <>
            <div className={`flex-1 overflow-hidden ${contentClass}`}>
              {/* ── Welcome ── */}
              {step === "welcome" && (
                <div className="h-full flex flex-col items-center text-center px-10">
                  <div className="flex-1 flex flex-col items-center justify-center">
                    {message.title && (
                      <h2 className="text-[22px] font-semibold text-brand-black/90 leading-snug">
                        {message.title}
                      </h2>
                    )}
                    {message.title_description && (
                      <p className="text-[15px] text-brand-black/50 mt-2 max-w-sm">
                        {message.title_description}
                      </p>
                    )}
                    {items.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => goTo(0, 1)}
                        className="mt-8 inline-flex items-center justify-center gap-2 h-[44px] px-8 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors"
                      >
                        Ver novedades
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleClose}
                        className="mt-8 inline-flex items-center justify-center h-[44px] px-8 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors"
                      >
                        Entendido
                      </button>
                    )}
                  </div>
                  <div className="shrink-0 w-44 h-44 mb-4">
                    <DotLottieReact
                      src="/images/lottie_animations/Rocket Launch.lottie"
                      autoplay
                      loop
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
              )}

              {/* ── Item (desktop: text left, media right) ── */}
              {currentItem &&
                typeof step === "number" &&
                (() => {
                  const mediaUrl = getMediaUrl(currentItem);
                  return (
                    <div className="flex h-full">
                      {/* Left: text + nav */}
                      <div className="flex-1 flex flex-col px-8 py-6 min-w-0">
                        {/* Dots at top-left */}
                        {items.length > 1 && (
                          <div className="flex items-center gap-1.5 mb-5">
                            {items.map((_, i) => (
                              <div
                                key={i}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                  i === step
                                    ? "w-5 bg-brand-primary"
                                    : "w-1.5 bg-neutral-300"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                        <div className="flex-1 flex flex-col justify-center">
                          <h3 className="text-[22px] font-semibold text-brand-black/90 leading-snug">
                            {currentItem.titulo}
                          </h3>
                          {currentItem.descripcion && (
                            <p className="text-[15px] text-brand-black/50 mt-2">
                              {currentItem.descripcion}
                            </p>
                          )}
                          <PuntosList puntos={currentItem.puntos} className="mt-5" />
                        </div>
                        {/* Nav buttons at bottom */}
                        {/* Con botón propio va en su renglón, a todo el ancho, como en el
                            celular: la columna de texto es el 40 % del modal y en una tablet
                            (768 px) quedan ~212 px, donde los tres en fila no entran. */}
                        <div className="mt-4 flex flex-col gap-3">
                          {boton && (
                            <button
                              type="button"
                              onClick={() => irAlBoton(boton)}
                              className="w-full inline-flex items-center justify-center min-h-[40px] px-6 py-2 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium text-center shadow-sm transition-colors"
                            >
                              {boton.texto}
                            </button>
                          )}
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                goTo(step === 0 ? "welcome" : step - 1, -1)
                              }
                              className="inline-flex items-center justify-center w-[40px] h-[40px] rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-brand-black/60 transition-colors shrink-0"
                            >
                              <ChevronLeft className="w-5 h-5" />
                            </button>
                            {isLastItem ? (
                              <button
                                type="button"
                                onClick={handleClose}
                                className={`inline-flex items-center justify-center h-[40px] px-6 rounded-md text-[15px] font-medium transition-colors ${boton ? "flex-1" : ""} ${claseNavegar}`}
                              >
                                Entendido
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => goTo(step + 1, 1)}
                                className={`inline-flex items-center justify-center gap-2 h-[40px] px-6 rounded-md text-[15px] font-medium transition-colors ${boton ? "flex-1" : ""} ${claseNavegar}`}
                              >
                                Siguiente
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Right: media (full height) */}
                      {mediaUrl && (
                        <div className="shrink-0 w-[60%] relative overflow-hidden bg-neutral-100">
                          <MediaElement
                            url={mediaUrl}
                            alt={currentItem.titulo}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
            {/* Desktop: no bottom bar for item screens (nav is inline on the left) */}
          </>
        )}
      </div>
    </div>
  );
};
